import { OriginBadge } from "@components/shared/OriginBadge";
import { SlideOverPanel } from "@components/shared/SlideOverPanel";
import { ARTEFACT_VERSION_HISTORY_QUERY } from "@lib/graphql/queries";
import { useChatStore } from "@lib/stores/chatStore";
import { MessageSquare } from "lucide-react";
import { useQuery } from "urql";
import type { ArtefactVersionEntry, OpenArtefact } from "./types";

interface VersionHistoryPanelProps {
  campaignId: string;
  open: OpenArtefact | null;
  onClose: () => void;
}

type QueryData = {
  campaign: {
    id: string;
    artefactVersionHistory: ArtefactVersionEntry[];
  } | null;
};

export function VersionHistoryPanel({ campaignId, open, onClose }: VersionHistoryPanelProps) {
  const [result] = useQuery<QueryData>({
    query: ARTEFACT_VERSION_HISTORY_QUERY,
    variables: open
      ? { id: campaignId, kind: kindToEnum(open.kind), artefactId: open.artefactId }
      : {},
    pause: !open,
  });

  const entries = result.data?.campaign?.artefactVersionHistory ?? [];

  return (
    <SlideOverPanel
      open={!!open}
      onClose={onClose}
      title={open ? `${open.label} — version history` : "Version history"}
    >
      {result.fetching && <p className="text-sm text-text-muted">Loading…</p>}
      {!result.fetching && entries.length === 0 && (
        <p className="text-sm text-text-muted">No versions recorded yet.</p>
      )}
      {entries.length > 0 && (
        <ul className="space-y-3">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className={`rounded border p-3 ${entry.effectiveTo === null ? "border-accent bg-card-hover" : "border-border bg-surface"}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-text-primary">v{entry.versionNumber}</span>
                <span className="text-xs text-text-muted">
                  {new Date(entry.effectiveFrom).toLocaleString()}
                </span>
              </div>
              {entry.changeSummary && (
                <p className="mt-1 text-sm italic text-text-secondary">{entry.changeSummary}</p>
              )}
              <div className="mt-1 flex items-center gap-2 text-xs text-text-muted">
                {entry.author && <span>by {entry.author.name}</span>}
                {entry.generationState &&
                  entry.generationState !== "draft" &&
                  entry.generationState !== "ready" && (
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
                      {entry.generationState}
                    </span>
                  )}
                <OriginBadge
                  origin={entry.origin}
                  actorSource={entry.actorSource}
                  model={entry.model}
                />
                {entry.generationRunChatThreadId && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-0.5 text-accent underline decoration-dotted underline-offset-2 hover:text-accent-hover"
                    data-testid="open-conversation-link"
                    onClick={() => {
                      useChatStore.getState().setCurrentThread(entry.generationRunChatThreadId!);
                      useChatStore.getState().open();
                    }}
                  >
                    <MessageSquare size={10} />
                    Open conversation
                  </button>
                )}
                {entry.effectiveTo === null && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                    Current
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </SlideOverPanel>
  );
}

function kindToEnum(kind: OpenArtefact["kind"]): string {
  return kind.toUpperCase();
}
